import { Maybe } from "@figurepos/fgr-lib-business-logic/lib/common/types"
import { TSetting } from "@figurepos/settings-sdk"
import { filter, flatten, isNil, map, reject } from "ramda"
import type { StateObject } from "../../redux/types"
import { getAccountImages, getSettings, isLocationsAndTerminalsAgendaOpened } from "../../data/selectors"
import type { Setting } from "../../data/types"
import { getSelectedLocationIdsFromGlobalFilter } from "../../filters/selectors"
import type { SettingsState } from "./reducer"
import { getCurrentPath, getCurrentPathName } from "../../router/selectors"
import {
    MIB_TIPPING_METHOD_PERCENTAGES,
    TIPPING_PRESELECTED_OPTION_INDEX,
} from "./mibSettings/MakeItButterSettingsForm"
import { getLocationAuthDataById, getLocationsAuthData, getTerminalAuthDataById } from "../../auth/selectors"
import { MainMenuIconValue } from "./mibSettings/components/MibMainMenuIconSelect"
import { getPricingById, getPricingsData } from "../pricingsAndPromotions/pricing/selectors"
import { DEFAULT_READY_DATE_CONFIGURATION, SettingFormItem, TerminalLocationIdPair, WeekStartDay } from "./types"
import { getMibServicePickerSelectedId } from "../../common/terminalPickers/selectors"
import { getCurrencyBasedSettingInDollars, getPercentageBasedSetting } from "./utils"
import {
    FirstCourseFiringBehaviorEnum,
    HalfToneImageFilterName,
    OrderItemsSortBy,
    PrintUnorderedItemsInAdvanceMode,
    ReceiptModifierGroupNameDisplayingEnum,
    SALE_RECEIPT_TYPES,
    SalesReceiptPrintingBehaviorUponAcceptingOrderEnum,
    ReadyScheduledTimePosition,
} from "./receiptSettings/types"
import type { HooSettingsResponse } from "../../../graphql/types"
import { TerminalType } from "../../../graphql/types"
import { FUTURE_ORDERING_DAYS_DEFAULT } from "./mibSettings/components/delivery/DeliveryFutureOrders"
import { pipeline } from "../../../utils/functional"
import { MultiSelectItem } from "../../uiKit/MultiSelect/MultiSelect"
import { LocationAuthData } from "../../auth/types"
import { getSelectedLocationId } from "../../common/locationsPicker/selectors"
import { getGratuityDefaultValues } from "./generalSettings/GeneralSettings/selectors"
import { TipCalculationMethod } from "@figurepos/fgr-lib-business-logic/lib/receipts/types"
import { logger } from "../../../utils/loggerWrap"

export type SettingType = "string" | "array" | "textAreaArray" | "other" | "arrayOfObjects"

export const getSettingsStateObject = (state: StateObject): SettingsState => state.settings

export const getSettingsImageUploadModalOpened = (state: StateObject): boolean =>
    getSettingsStateObject(state).imageModalOpened

export const isMibLocationsModalOpened = (state: StateObject) => getSettingsStateObject(state).mibModal

export const getHooSettingsData = (state: StateObject): Maybe<HooSettingsResponse> =>
    getSettingsStateObject(state).hooSettings

export const getSelectedTerminalIds = (state: StateObject) => {
    const ids = getSettingsStateObject(state).selectedTerminalIds

    if (shouldFilterMibTerminals(state)) {
        return ids.filter(id => {
            const t = getTerminalAuthDataById(state, id)
            return t && t.type !== TerminalType.Mib
        })
    } else {
        return ids
    }
}

export const getTerminalIdsWithLocationId = (state: StateObject, terminalIds: string[]): TerminalLocationIdPair[] => {
    const res = terminalIds.map(id => {
        const t = getTerminalAuthDataById(state, id)

        if (!t) {
            return null
        }

        return {
            locationId: t.locationId || null,
            terminalId: t.id || null,
        }
    })
    // @ts-expect-error
    return res.filter(a => a)
}

export const isSettingSaving = (state: StateObject) => getSettingsStateObject(state).isSaving

export const shouldFilterMibTerminals = (state: StateObject) => {
    if (isMibSettingsAgendaOpened(state) || isLocationsAndTerminalsAgendaOpened(state)) {
        return false
    }

    return true
}

export const getDataForSettingsTerminalPicker = (state: StateObject): MultiSelectItem[] => {
    const locations = getLocationsAuthData(state)

    return pipeline(
        locations,
        map((location: LocationAuthData) => {
            const terminals = location.terminals.filter(
                terminal =>
                    terminal.type === TerminalType.Mpos ||
                    terminal.type === TerminalType.Register ||
                    terminal.type === TerminalType.Kiosk
            )
            if (terminals.length === 0) {
                return null
            }

            return {
                id: location.id,
                label: location.name,
                labelOnly: true,
                children: terminals.map(terminal => ({
                    id: terminal.id,
                    label: terminal.name,
                })),
            }
        }),
        flatten,
        reject(isNil)
    ) as MultiSelectItem[]
}

export const isMibSettingsAgendaOpened = (state: StateObject) =>
    getCurrentPathName(state) === "/app/makeItButterSettings"

export const isDeliverySettingsAgendaOpened = (state: StateObject) =>
    getCurrentPathName(state) === "/app/deliverySettings"

export const getSettingByKey = (state: StateObject, key: string): Maybe<Setting> => {
    const settings = getSettings(state)

    if (!settings || settings.length < 1) {
        return null
    }

    return settings.find(s => s.key === key)
}

export const getSettingByKeyAndLocationId = (state: StateObject, key: string, locationId: string): Maybe<Setting> => {
    const settings = getSettingsByKey(state, key)

    if (!settings || settings.length < 1) {
        return null
    }

    return settings.find(s => s.location_id === locationId)
}

export const getSettingValueByKeyAndLocationId = (state: StateObject, key: string, locationId: string): Maybe<any> => {
    const value = getSettingByKeyAndLocationId(state, key, locationId)?.value
    return value ? JSON.parse(value) : null
}

export const getSettingByKeyAndTerminalId = (
    state: StateObject,
    key: string,
    locationId: Maybe<string>,
    terminalId: string
): Maybe<Setting> => {
    const settings = getSettingsByKey(state, key)

    if (!settings || settings.length === 0) {
        return null
    }

    return settings.find(s =>
        locationId ? s.location_id === locationId && s.terminal_id === terminalId : s.terminal_id === terminalId
    )
}

export const getSettingsByKey = (state: StateObject, key: string): Setting[] => {
    const settings = getSettings(state)

    if (!settings || settings.length < 1) {
        return []
    }

    return settings.filter(s => s.key === key).filter(a => a)
}

export const parseSettingValue = (value: string, type: Maybe<string> = "other"): any => {
    try {
        if (value === undefined) {
            return null
        }

        if (type === "string") {
            if (!value || value === "null") {
                return null
            }

            return value
        }

        if (type === "array") {
            if (!value || !JSON.parse(value)) {
                return ""
            }

            return JSON.parse(value).join(",")
        }

        if (type === "textAreaArray") {
            if (!value || !JSON.parse(value)) {
                return ""
            }

            return JSON.parse(value).join("\n")
        }

        if (type === "arrayOfObjects") {
            if (!value || !JSON.parse(value)) {
                return null
            }

            return JSON.parse(value)
        }

        return JSON.parse(value)
    } catch (e) {
        logger.error("parseSettingValue - could not JSON.parse setting value", {
            error: e,
            value: value,
            type: type,
        })
    }

    return null
}

// Todo inspect with Trubi, write better --> Use Reduce/Some
const isAllValuesTheSame = (settings: Setting[]): boolean => {
    let tempValue = null
    let result = true
    settings.forEach((s: Setting, index: number) => {
        if (index === 0) {
            tempValue = s.value
        } else if (s.value !== tempValue) {
            result = false
        }
    })
    return result
}

export const handleAccountSettingFormItem = (
    state: StateObject,
    key: string,
    type: Maybe<SettingType> = "other",
    defaultValue: Maybe<any> = null
): SettingFormItem => {
    const setting = getSettingByKey(state, key)

    if (!setting) {
        return {
            [key]: {
                value: defaultValue,
                // Default Value
                indeterminate: false,
            },
        }
    }

    return {
        [key]: {
            value: parseSettingValue(setting.value, type),
            indeterminate: false,
        },
    }
}

export const getSettingFormItem = (
    key: string, // FUTURE_DAYS
    type: Maybe<SettingType> = "other",
    defaultValue: Maybe<any> = null,
    selectedIds: string[],
    selectedSettings: Setting[], // the actual setting data
    overrideIndeterminate: boolean = false
) => {
    if (overrideIndeterminate) {
        return {
            [key]: {
                value: undefined,
                indeterminate: true,
            },
        }
    }

    // All Settings Are Defined for All selected locations/terminals
    if (selectedIds.length === selectedSettings.length) {
        return isAllValuesTheSame(selectedSettings)
            ? {
                  [key]: {
                      value:
                          selectedSettings[0] && selectedSettings[0].value != null
                              ? parseSettingValue(selectedSettings[0].value, type)
                              : defaultValue,
                      indeterminate: false,
                  },
              }
            : {
                  [key]: {
                      value: undefined,
                      // Multiple Values
                      indeterminate: true,
                  },
              }
    } else if (selectedSettings.length === 0) {
        // No defined settings
        return {
            [key]: {
                value: defaultValue,
                // Default Value
                indeterminate: false,
            },
        }
    } // All defined settings has same value and also same value as default
    else if (
        isAllValuesTheSame(selectedSettings) &&
        parseSettingValue(selectedSettings[0].value, type) === defaultValue
    ) {
        return {
            [key]: {
                value:
                    selectedSettings[0] && selectedSettings[0].value != null
                        ? parseSettingValue(selectedSettings[0].value, type)
                        : defaultValue,
                indeterminate: false,
            },
        }
    } else {
        return {
            [key]: {
                value: undefined,
                // Multiple Values
                indeterminate: true,
            },
        }
    }
}

export const handleLocationSettingFormItem = (
    state: StateObject,
    key: string,
    type: Maybe<SettingType> = "other",
    defaultValue: Maybe<any> = null
): SettingFormItem => {
    const selectedLocationsIds = getSelectedLocationIdsFromGlobalFilter(state)
    const ass: Setting[] = getSettingsByKey(state, key)?.filter((s: Setting) =>
        s.location_id ? selectedLocationsIds.includes(s.location_id) : false
    )
    return getSettingFormItem(key, type, defaultValue, selectedLocationsIds, ass)
}

export const handleSingleLocationSettingFormItem = (
    state: StateObject,
    key: string,
    type: Maybe<SettingType> = "other",
    defaultValue: Maybe<any> = null
): SettingFormItem => {
    const selectedLocationId = getSelectedLocationId(state)
    const ass: Setting[] = getSettingsByKey(state, key)?.filter((s: Setting) =>
        s.location_id ? selectedLocationId === s.location_id : false
    )
    return getSettingFormItem(key, type, defaultValue, selectedLocationId ? [selectedLocationId] : [], ass)
}

export const getSettingValueForLocationId = (
    state: StateObject,
    key: string,
    locationId: string,
    type: Maybe<SettingType> = "other",
    defaultValue: Maybe<any> = null
): SettingFormItem => {
    const ass: Setting[] = getSettingsByKey(state, key)?.filter((s: Setting) => locationId === s.location_id)
    return getSettingFormItem(key, type, defaultValue, [locationId], ass)
}

export const handleTerminalSettingFormItem = (
    state: StateObject,
    key: string,
    type: Maybe<SettingType> = "other",
    defaultValue: Maybe<any> = null,
    overrideIndeterminate: boolean = false
): SettingFormItem => {
    const selectedTerminalIds = getSelectedTerminalIds(state)
    // @ts-expect-error
    const ass = getSettingsByKey(state, key).filter(s => selectedTerminalIds.includes(s.terminal_id))
    return getSettingFormItem(key, type, defaultValue, selectedTerminalIds, ass, overrideIndeterminate)
}
// Account Settings

export const getGeneralSettings = (state: StateObject): SettingFormItem => ({
    ...handleAccountSettingFormItem(state, "MIDNIGHT_OFFSET"),
    ...handleAccountSettingFormItem(state, "BUSINESS_START_OF_WEEK_DAY", "string", "Monday"),
})
// Terminal Based Settings

export const getCardProcessingSettings = (state: StateObject): SettingFormItem => ({
    ...handleTerminalSettingFormItem(state, "SERVICES_PAYMENTS_OFFLINE_CC_ENABLED", "other", true),
    ...handleTerminalSettingFormItem(state, "APP_SIGNATURE_SCREEN_DISABLED", "other", true),
    ...handleTerminalSettingFormItem(state, "APP_SIGNATURE_SCREEN_SKIP_ENABLED", "other", false),
    ...handleTerminalSettingFormItem(state, "APP_CARD_PAYMENT_QUICK_CHECKOUT_ENABLED", "other", false),
    ...handleTerminalSettingFormItem(state, "APP_PAYMENT_CONFIRMATION_TIMEOUT"),
})

export const getBarcodeSettings = (state: StateObject): SettingFormItem => ({
    ...handleTerminalSettingFormItem(state, "BARCODE_SCANNER_SCREEN_ENABLED", "other", false),
    ...handleTerminalSettingFormItem(state, "BARCODE_LOAD_ORDER_BY_ASSIGNMENT_ACTIVATED", "other", false),
    ...handleTerminalSettingFormItem(state, "BARCODE_ORDER_MERGING_UPON_SCANNING_ENABLED", "other", false),
})

export const getGratuitySettings = (state: StateObject): SettingFormItem => {
    const settings = {
        ...handleTerminalSettingFormItem(state, "MINIMAL_GUEST_COUNT_FOR_AUTOMATIC_GRATUITY", "other", 0),
        ...handleTerminalSettingFormItem(state, "AUTOMATIC_GRATUITY_PERCENTAGE_VALUE", "string", 0),
        ...handleTerminalSettingFormItem(state, "TIP_SUGGESTION_ACTIVE_GRATUITY_ENABLED", "other", true),
    }

    // For backward compatibility we want to enable the gratuity by default for merchants who already used automatic gratuity
    const { defaultEnabled, overrideIndeterminate } = getGratuityDefaultValues(state)

    return {
        ...handleTerminalSettingFormItem(state, "GRATUITY_ENABLED", "other", defaultEnabled, overrideIndeterminate),
        ...settings,
    }
}

export const getSalesInputsSettings = (state: StateObject): SettingFormItem => ({
    ...handleTerminalSettingFormItem(state, "APP_SALES_INPUT_DEFAULT", "string", "schematized"),
    ...handleTerminalSettingFormItem(state, "SWIPE_UP_SCHEMATIZED_VIEW", "other", true),
    ...getReadyDateConfiguration(state),
    ...handleTerminalSettingFormItem(state, "TABLE_MANAGEMENT_ENABLED", "other", false),
    ...handleTerminalSettingFormItem(state, "SEATING_MANAGEMENT_ENABLED", "other", false),
    ...handleTerminalSettingFormItem(state, "SCALE_ENABLED", "other", false),
    ...handleTerminalSettingFormItem(state, "SEATING_MANAGEMENT_MULTIPLE_SEATING_ENABLED", "other", false),
    ...handleTerminalSettingFormItem(state, "SCHEMATIZED_INVENTORY_LOW_ALERT_ENABLED", "other", true),
    ...handleTerminalSettingFormItem(state, "SCHEMATIZED_SHOW_ITEM_ICON", "other", true),
})

const getReadyDateConfiguration = (state: StateObject): SettingFormItem => {
    const config = handleTerminalSettingFormItem(state, "READY_DATE_CONFIGURATION", "other", null)[
        "READY_DATE_CONFIGURATION"
    ]
    if (config.value !== null) {
        return {
            READY_DATE_CONFIGURATION: config,
        }
    }
    const isEnabled = handleTerminalSettingFormItem(state, "READY_DATE_ENABLED", "other", false)["READY_DATE_ENABLED"]
    return {
        READY_DATE_CONFIGURATION: {
            value: isEnabled.indeterminate
                ? undefined
                : {
                      ...DEFAULT_READY_DATE_CONFIGURATION,
                      isEnabled: isEnabled.value,
                  },
            indeterminate: isEnabled.indeterminate,
        },
    }
}

export const getSalesBehaviourSettings = (state: StateObject): SettingFormItem => ({
    ...handleTerminalSettingFormItem(state, "APP_ORDER_ASSIGNMENT_REQUIRED", "other", false),
    ...handleTerminalSettingFormItem(state, "APP_ORDER_CUSTOMER_REQUIRED", "other", false),
    ...handleTerminalSettingFormItem(state, "APP_ORDER_READY_DATE_REQUIRED", "other", false),
    ...handleTerminalSettingFormItem(state, "APP_ORDER_NEW_ORDER_STAY_IN_CATEGORY", "other", false),
    ...handleTerminalSettingFormItem(state, "GUEST_COUNT_ENABLED", "other", false),
    ...handleTerminalSettingFormItem(state, "APP_ORDER_GUEST_COUNT_REQUIRED", "other", false),
    ...handleTerminalSettingFormItem(state, "ACCEPT_TIPS", "other", true),
    ...handleTerminalSettingFormItem(state, "APP_SIGNATURE_CAPTURE_ON_ALL_SALES", "other", false),
    ...handleTerminalSettingFormItem(state, "SHOW_SCREEN_DIGITAL_RECEIPT_CONFIRMATION", "other", false),
    ...handleTerminalSettingFormItem(state, "PRE_AUTH_ENABLED", "other", false),
    ...getCurrencyBasedSettingInDollars(
        handleTerminalSettingFormItem(state, "DEFAULT_PRE_AUTHORIZATION_AMOUNT", "other", 100),
        "DEFAULT_PRE_AUTHORIZATION_AMOUNT"
    ),
})

export const getOtherSettings = (state: StateObject): SettingFormItem => ({
    ...handleTerminalSettingFormItem(state, "PRINTER_CLOCK_OUT_RECEIPT", "other", true),
    ...handleTerminalSettingFormItem(state, "PRINTER_NO_SALE_PAY_IN_OUT_RECEIPT", "other", true),
    ...handleTerminalSettingFormItem(state, "PRINTER_NO_SALE_CHANGE_RECEIPT", "other", false),
    ...handleTerminalSettingFormItem(state, "VOID_REASONS_ENABLED", "other", false),
    ...handleTerminalSettingFormItem(state, "RECEIPT_PREFIX_HIGHLIGHTED_ORDER_NUMBER", "other", false),
    ...handleTerminalSettingFormItem(state, "APP_DISABLE_CLOCK_OUT_WITH_OPEN_ORDERS", "other", false),
    ...handleTerminalSettingFormItem(state, "SIGN_IN_SCREEN_CAMERA_ENABLED", "other", false),
    ...handleTerminalSettingFormItem(state, "SIGN_IN_SCREEN_CAMERA_CAPTURE", "other", false),
    ...handleTerminalSettingFormItem(state, "APP_AUTO_SIGN_OUT_TIMEOUT"),
})

export const getReasonCodesSettings = (state: StateObject): SettingFormItem => ({
    ...handleTerminalSettingFormItem(state, "DECLINE_REASON_REQUIRED", "other", false),
    ...handleTerminalSettingFormItem(
        state,
        "DECLINE_REASONS_ARRAY",
        "array",
        "Out of Stock/Items,Delivery is not available at this time,Delivery is out of range"
    ),
    ...handleTerminalSettingFormItem(state, "REFUND_REASON_REQUIRED", "other", false),
    ...handleTerminalSettingFormItem(
        state,
        "REFUND_REASONS_ARRAY",
        "array",
        "Out of Stock/Items,Returned Item,Late Order,Mistake Order"
    ),
    ...handleTerminalSettingFormItem(state, "ITEM_VOID_REASON_REQUIRED", "other", false),
    ...handleTerminalSettingFormItem(
        state,
        "ITEM_VOID_REASONS_ARRAY",
        "array",
        "Employee Mistake - Order Error,Employee Mistake - Production Error,Out of Stock/Items,Customer Changed Mind"
    ),
    ...handleTerminalSettingFormItem(state, "TRANSACTION_VOID_REASON_REQUIRED", "other", false),
    ...handleTerminalSettingFormItem(
        state,
        "TRANSACTION_VOID_REASONS_ARRAY",
        "array",
        "Employee Mistake,Customer Changed Payment Method"
    ),
})

export const getMidnightOffset = (state: StateObject): number => {
    const offsetSetting = getSettingByKey(state, "MIDNIGHT_OFFSET")
    return offsetSetting ? Number(parseSettingValue(offsetSetting.value)) : 0
}

export const getBusinessStartOfWeekDay = (state: StateObject): WeekStartDay => {
    const startOfWeekSetting = getSettingByKey(state, "BUSINESS_START_OF_WEEK_DAY")
    return startOfWeekSetting?.value ? parseSettingValue(startOfWeekSetting.value, "string") : "Monday"
}

// Receipt Settings SALES //

export const getSalesReceiptSettingsNew = (state: StateObject): SettingFormItem => ({
    ...handleTerminalSettingFormItem(state, "PRINTER_MERCHANT_COPY", "other", 1),
    ...handleTerminalSettingFormItem(state, "PRINTER_CUSTOMER_COPY", "other", 1),
    ...handleTerminalSettingFormItem(state, "RECEIPT_HEADER", "textAreaArray", ""),
    ...handleTerminalSettingFormItem(state, "RECEIPT_BUSINESS_INFO", "textAreaArray", ""),
    ...handleTerminalSettingFormItem(state, "RECEIPT_FOOTER", "textAreaArray", ""),
    ...handleTerminalSettingFormItem(state, "RECEIPT_HIGHLIGHTED_ORDER_NUMBER_SALE", "other", false),
    ...handleTerminalSettingFormItem(state, "RECEIPT_HIGHLIGHTED_ORDER_NUMBER_DIGITS", "other", 2),
    ...handleTerminalSettingFormItem(state, "RECEIPT_PREFIX_HIGHLIGHTED_ORDER_NUMBER", "other", false),
    ...handleTerminalSettingFormItem(state, "BUSINESS_LOGO", "string", null),
    ...handleTerminalSettingFormItem(
        state,
        "BUSINESS_LOGO_CIFILTER_NAME",
        "string",
        HalfToneImageFilterName.CIDotScreen
    ),
    ...handleTerminalSettingFormItem(state, "BUSINESS_LOGO_CIFILTER_WIDTH", "other", 9),
    ...handleTerminalSettingFormItem(state, "RECEIPT_TIP_GUIDE", "other", true),
    ...handleTerminalSettingFormItem(state, "RECEIPT_SHOW_TIP_GUIDE_ON_NO_TIP", "other", false),
    ...handleTerminalSettingFormItem(state, "RECEIPT_TIP_GUIDE_CONFIG", "array", "15000,18000,20000,25000"),
    ...handleTerminalSettingFormItem(state, "RECEIPT_DISPLAY_ORDER_NOTE_ON_SALES", "other", false),
    ...handleTerminalSettingFormItem(state, "RECEIPT_DISPLAY_ORDER_ITEM_NOTE_ON_SALES", "other", false),
    ...handleTerminalSettingFormItem(state, "RECEIPT_DISPLAY_CUSTOMER_DETAIL_SALE", "other", false),
    ...handleTerminalSettingFormItem(state, "RECEIPT_PRINT_PICK_UP_BARCODE", "other", false),
    ...handleLocationSettingFormItem(state, "ACCEPT_TIPS", "other", true),
    ...handleTerminalSettingFormItem(state, "APP_SIGNATURE_CAPTURE_ON_ALL_SALES", "other", false),
    ...handleTerminalSettingFormItem(state, "RECEIPT_GROUP_ITEMS_ENABLED", "other", true),
    ...handleTerminalSettingFormItem(state, "RECEIPT_SALE_ORDER_DELIVERY_PARTNER_HIGHLIGHT_ENABLED", "other", true),
    ...handleTerminalSettingFormItem(state, "RECEIPT_SALE_ORDER_EXTERNAL_SERVICE_HIGLIGHT_ENABLED", "other", false),
    ...handleTerminalSettingFormItem(state, "TIPPING_METHOD", "string", "smart"),
    ...handleTerminalSettingFormItem(state, TIPPING_PRESELECTED_OPTION_INDEX, "other", null),
    ...handleTerminalSettingFormItem(state, "RECEIPT_TIP_GUIDE_VARIANT", "string", "classic"),
    ...handleTerminalSettingFormItem(state, "TIP_CALCULATION_METHOD", "string", TipCalculationMethod.Subtotal),
    ...handleTerminalSettingFormItem(state, "RECEIPT_DISPLAY_GUEST_COUNT_ON_SALES", "other", false),
    ...handleTerminalSettingFormItem(state, "RECEIPT_DISPLAY_TIP_GUIDE_ON_SALES_CHECK", "other", false),
    ...handleTerminalSettingFormItem(state, "RECEIPT_DISPLAY_FEES_DETAILS_ON_CHECK_SALES", "other", true),
    ...handleTerminalSettingFormItem(
        state,
        "SALES_RECEIPT_PRINTING_BEHAVIOR_UPON_ACCEPTING_ORDER",
        "string",
        SalesReceiptPrintingBehaviorUponAcceptingOrderEnum.PrintMerchantAndCustomerReceipt
    ),
    ...handleTerminalSettingFormItem(state, "RECEIPT_PROMINENT_CUSTOMER_NAME_SALE", "other", false),
    ...handleTerminalSettingFormItem(
        state,
        "RECEIPT_SALE_ESTIMATED_READY_TIME_FOR_ASAP_ORDERS_ENABLED",
        "other",
        false
    ),
    ...handleTerminalSettingFormItem(state, "RECEIPT_PRINT_CARD_ENTRY_METHOD_SECTION_ON_SALES", "other", []),
    ...handleTerminalSettingFormItem(state, "RECEIPT_PRINT_ORDER_ITEM_SECTION_ON_SALES", "other", SALE_RECEIPT_TYPES),
    ...handleTerminalSettingFormItem(
        state,
        "RECEIPT_PRINT_OPTIONS_AND_MODIFIERS_ON_SALES",
        "other",
        SALE_RECEIPT_TYPES
    ),
    ...handleTerminalSettingFormItem(
        state,
        "RECEIPT_PRINT_OPTIONS_AND_MODIFIERS_PRICES_ON_SALES",
        "other",
        SALE_RECEIPT_TYPES
    ),
    ...handleTerminalSettingFormItem(
        state,
        "RECEIPT_SALE_READY_SCHEDULED_TIME_POSITION",
        "string",
        ReadyScheduledTimePosition.Top
    ),
    ...getDisplayCountOnSalesReceipt(state),
})

export const getEmailReceiptSettingsNew = (state: StateObject): SettingFormItem => ({
    ...handleTerminalSettingFormItem(state, "RECEIPT_HEADER_EMAIL", "textAreaArray", ""),
    ...handleTerminalSettingFormItem(state, "RECEIPT_BUSINESS_INFO_EMAIL", "textAreaArray", ""),
    ...handleTerminalSettingFormItem(state, "CUSTOMER_FEEDBACK_BUTTONS_ENABLED", "other", true),
    ...handleTerminalSettingFormItem(state, "RECEIPT_TIP_GUIDE", "other", true),
    ...handleTerminalSettingFormItem(state, "RECEIPT_FOOTER_EMAIL", "textAreaArray", ""),
    ...handleTerminalSettingFormItem(state, "BUSINESS_EMAIL_LOGO", "string", null),
    ...handleTerminalSettingFormItem(state, "RECEIPT_EMAIL_PICK_UP_BARCODE", "other", false),
    ...handleTerminalSettingFormItem(state, "ACCOUNT_FACEBOOK", "string", null),
    ...handleTerminalSettingFormItem(state, "ACCOUNT_TIKTOK", "string", null),
    ...handleTerminalSettingFormItem(state, "ACCOUNT_INSTAGRAM", "string", null),
    ...handleTerminalSettingFormItem(state, "ACCOUNT_TWITTER", "string", null),
    ...handleTerminalSettingFormItem(state, "ACCOUNT_SOCIAL_LINKS_ENABLED", "other", true),
})

export const getLabelPrintingSettings = (state: StateObject): SettingFormItem => ({
    ...handleTerminalSettingFormItem(state, "APP_LABEL_PRINTING_ENABLED", "other", false),
    ...handleTerminalSettingFormItem(state, "LABEL_PRINTER_SELECTED_PAPER_TYPE", "string", null),
})

export const getTagPrintingSettings = (state: StateObject): SettingFormItem => ({
    ...handleTerminalSettingFormItem(state, "APP_TAG_PRINTING_ENABLED", "other", false),
    ...handleTerminalSettingFormItem(state, "RECEIPT_SHOW_ITEM_NUMBER_TAG", "other", true),
})

export const getTimesheetSettings = (state: StateObject): SettingFormItem => ({
    ...handleTerminalSettingFormItem(state, "RECEIPT_SHOW_SIGNATURE_CLOCK_OUT", "other", false),
})

export const getNoSaleSettings = (state: StateObject): SettingFormItem => ({
    ...handleTerminalSettingFormItem(state, "RECEIPT_SHOW_SIGNATURE_NO_SALE", "other", false),
})

export const getOrderKitchenReceiptSettings = (state: StateObject): SettingFormItem => ({
    ...handleTerminalSettingFormItem(state, "APP_KITCHEN_ORDERING_ENABLED", "other", false),
    ...handleTerminalSettingFormItem(state, "ORDER_BUTTON_MODE", "string", null),
    ...handleTerminalSettingFormItem(state, "APP_KITCHEN_VOID_RECEIPT_ENABLED", "other", true),
    ...handleTerminalSettingFormItem(state, "RECEIPT_DISPLAY_GUEST_COUNT_ON_ORDER", "other", false),
    ...handleTerminalSettingFormItem(state, "APP_COURSE_LINE_ENABLED", "other", false),
    ...handleTerminalSettingFormItem(state, "APP_COURSE_LINE_MODE", "string", null),
    ...handleTerminalSettingFormItem(state, "RECEIPT_SEPARATE_MULTI_QUANTITY_ITEMS_KITCHEN", "other", false),
    ...handleTerminalSettingFormItem(state, "RECEIPT_ADD_CHECKBOXES_NEXT_TO_ITEMS_KITCHEN", "other", false),
    ...handleTerminalSettingFormItem(state, "APP_KITCHEN_ORDERING_PRESELECT_ALL", "other", false),
    ...handleTerminalSettingFormItem(state, "APP_KITCHEN_ORDERING_AUTO_ORDER_UNORDERED_ITEMS", "other", false),
    ...handleTerminalSettingFormItem(state, "UNIT_QUANTITY_CHANGE_AFTER_ORDERING_ENABLED", "other", false),
    ...handleTerminalSettingFormItem(state, "RECEIPT_EXTRA_TOP_SPACE_KITCHEN", "other", false),
    ...handleTerminalSettingFormItem(state, "RECEIPT_KITCHEN_ITEMS_SORT_BY", "string", OrderItemsSortBy.Timestamp),
    ...handleTerminalSettingFormItem(state, "AUTOMATICALLY_PRINT_CHECK_RECEIPT_UPON_ORDER_ENABLED", "other", false),
    ...handleTerminalSettingFormItem(state, "RECEIPT_DISPLAY_PRICES_ON_ORDER", "other", false),
    ...handleTerminalSettingFormItem(state, "RECEIPT_DISPLAY_COUNT_ON_ORDER", "other", true),
    ...handleTerminalSettingFormItem(state, "RECEIPT_HIGHLIGHTED_ORDER_NUMBER_KITCHEN", "other", false),
    ...handleTerminalSettingFormItem(state, "RECEIPT_PREFIX_HIGHLIGHTED_ORDER_NUMBER", "other", false),
    ...handleTerminalSettingFormItem(state, "RECEIPT_HIGHLIGHTED_ORDER_NUMBER_DIGITS", "other", 2),
    ...handleTerminalSettingFormItem(state, "RECEIPT_HIGHLIGHT_ASSIGNMENT_KITCHEN", "other", false),
    ...handleTerminalSettingFormItem(state, "RECEIPT_HIGHLIGHT_CUSTOMER_KITCHEN", "other", false),
    ...handleTerminalSettingFormItem(state, "RECEIPT_KITCHEN_ORDER_DELIVERY_PARTNER_HIGHLIGHT_ENABLED", "other", true),
    ...handleTerminalSettingFormItem(state, "RECEIPT_KITCHEN_ORDER_EXTERNAL_SERVICE_HIGLIGHT_ENABLED", "other", false),
    ...handleTerminalSettingFormItem(state, "RECEIPT_KITCHEN_HIGHLIGHT_UNORDERED_MODIFIERS", "other", true),
    ...handleTerminalSettingFormItem(state, "ACTIVATE_BUZZER", "other", false),
    ...handleTerminalSettingFormItem(state, "ORDER_RECEIPT_GROUP_ITEMS_ENABLED", "other", false),
    ...handleTerminalSettingFormItem(state, "RECEIPT_KITCHEN_ORDER_NOTE_ENABLED", "other", true),
    ...handleTerminalSettingFormItem(
        state,
        "RECEIPT_KITCHEN_ESTIMATED_READY_TIME_FOR_ASAP_ORDERS_ENABLED",
        "other",
        false
    ),
    ...handleTerminalSettingFormItem(state, "RECEIPT_KITCHEN_HIGHLIGHT_ASSIGNMENT_AT_THE_BOTTOM", "other", false),
    ...handleTerminalSettingFormItem(state, "RECEIPT_KITCHEN_BIGGER_TEXT_FOR_ITEMS_SECTION", "other", false),
    ...handleTerminalSettingFormItem(state, "RECEIPT_KITCHEN_BIGGER_ASSIGNMENT", "other", false),
    ...handleTerminalSettingFormItem(
        state,
        "RECEIPT_KITCHEN_MODIFIER_GROUP_NAME_DISPLAYING",
        "string",
        ReceiptModifierGroupNameDisplayingEnum.ForGroupPriced
    ),
    ...handleTerminalSettingFormItem(
        state,
        "RECEIPT_KITCHEN_BIGGER_TEXT_FOR_ITEMS_SECTION_PER_PRINTER_TYPE",
        "string",
        null
    ),
    ...handleTerminalSettingFormItem(state, "RECEIPT_KITCHEN_BIGGER_ASSIGNMENT_PER_PRINTER_TYPE", "string", null),
    ...handleTerminalSettingFormItem(state, "RECEIPT_EXTRA_TOP_SPACE_KITCHEN_PER_PRINTER_TYPE", "string", null),
    ...handleTerminalSettingFormItem(
        state,
        "RECEIPT_KITCHEN_READY_SCHEDULED_TIME_POSITION",
        "string",
        ReadyScheduledTimePosition.Top
    ),
    ...getKitchenPrintUnorderedItemsInAdvanceMode(state),
})

// APP_KITCHEN_PRINT_UNORDERED_ITEMS_IN_ADVANCE_MODE is new key that extends the old first firing behavior setting
//  the old setting is deprecated, and we calculate value for the new one based on the old one
const getKitchenPrintUnorderedItemsInAdvanceMode = (state: StateObject): SettingFormItem => {
    const newKey = "APP_KITCHEN_PRINT_UNORDERED_ITEMS_IN_ADVANCE_MODE"
    const newSetting = handleTerminalSettingFormItem(state, newKey, "string", null)

    if (newSetting[newKey]?.value == null) {
        const oldKey = "APP_FIRST_COURSE_FIRING_BEHAVIOR"
        const oldSetting = handleTerminalSettingFormItem(
            state,
            oldKey,
            "string",
            FirstCourseFiringBehaviorEnum.FullOrder
        )
        const fallbackValue =
            oldSetting[oldKey]?.value === FirstCourseFiringBehaviorEnum.FullOrder
                ? PrintUnorderedItemsInAdvanceMode.FirstPrint
                : PrintUnorderedItemsInAdvanceMode.Never

        newSetting[newKey] = {
            ...newSetting[newKey],
            value: fallbackValue,
        }
        return newSetting
    }
    return newSetting
}

export const getCardPaymentSettings = (state: StateObject): SettingFormItem => ({
    ...handleLocationSettingFormItem(state, "CARD_TRANSACTIONS_ORDER_LIST_OPTIONS_ENABLED", "other", false),
})

export const getGatewaySettings = (state: StateObject): SettingFormItem => ({
    // POS Gateway Settings
    ...handleSingleLocationSettingFormItem(state, "CC_GATEWAYS", "array", ""),
    ...handleSingleLocationSettingFormItem(state, "CC_CARD_POINTE_MID", "string", null),
    ...handleSingleLocationSettingFormItem(state, "CC_CONVERGE_MID", "string", null),
    ...handleSingleLocationSettingFormItem(state, "CC_CONVERGE_USER_ID", "string", null),
    ...handleSingleLocationSettingFormItem(state, "CC_CONVERGE_PIN", "string", null),
    ...handleSingleLocationSettingFormItem(state, "CC_UNIGATE_CUSTOMER_CODE", "string", null),
    ...handleSingleLocationSettingFormItem(state, "CC_UNIGATE_USERNAME", "string", null),
    ...handleSingleLocationSettingFormItem(state, "CC_UNIGATE_PASSWORD", "string", null),
    // KIOSK Gateway Settings
    ...handleSingleLocationSettingFormItem(state, "CC_KIOSK_GATEWAYS", "array", ""),
    ...handleSingleLocationSettingFormItem(state, "CC_CARD_POINTE_KIOSK_MID", "string", null),
    ...handleSingleLocationSettingFormItem(state, "CC_CONVERGE_KIOSK_MID", "string", null),
    ...handleSingleLocationSettingFormItem(state, "CC_CONVERGE_KIOSK_USER_ID", "string", null),
    ...handleSingleLocationSettingFormItem(state, "CC_CONVERGE_KIOSK_PIN", "string", null),
    ...handleSingleLocationSettingFormItem(state, "CC_UNIGATE_KIOSK_CUSTOMER_CODE", "string", null),
    ...handleSingleLocationSettingFormItem(state, "CC_UNIGATE_KIOSK_USERNAME", "string", null),
    ...handleSingleLocationSettingFormItem(state, "CC_UNIGATE_KIOSK_PASSWORD", "string", null),
    // Online Ordering / MIB Gateway Settings
    ...handleSingleLocationSettingFormItem(state, "CC_MIB_GATEWAYS", "array", ""),
    ...handleSingleLocationSettingFormItem(state, "CC_CARD_POINTE_MIB_MID", "string", null),
    ...handleSingleLocationSettingFormItem(state, "CC_CONVERGE_MIB_MID", "string", null),
    ...handleSingleLocationSettingFormItem(state, "CC_CONVERGE_MIB_USER_ID", "string", null),
    ...handleSingleLocationSettingFormItem(state, "CC_CONVERGE_PIN_ONLINE", "string", null),
    ...handleSingleLocationSettingFormItem(state, "CC_ONLINE_ORDERING_APPLE_PAY_ENABLED", "other", false),
    ...handleSingleLocationSettingFormItem(state, "CC_ONLINE_ORDERING_IS_ZIP_REQUIRED", "other", true),
    ...handleSingleLocationSettingFormItem(state, "CC_ONLINE_ORDERING_IS_CVV_REQUIRED", "other", true),
    ...handleSingleLocationSettingFormItem(state, "CC_TSYS_MID", "string", null),
    ...handleSingleLocationSettingFormItem(state, "CC_TSYS_USER_ID", "string", null),
    ...handleSingleLocationSettingFormItem(state, "CC_TSYS_PASSWORD", "string", null),
    ...handleSingleLocationSettingFormItem(state, "CC_SEAMLESSPAY_MIB_MID", "string", null),
})

export type BoltDeviceTerminalSettings = {
    terminalId: string
    locationId: string
    CC_BOLT_DEVICE_ID: Maybe<string>
    CC_BOLT_DEVICE_AUDIO_ENABLED: Maybe<boolean>
}

export const getBoltDeviceSettings = (state: StateObject): BoltDeviceTerminalSettings[] => {
    const selectedLocationId = getSelectedLocationId(state)
    const location = selectedLocationId ? getLocationAuthDataById(state, selectedLocationId) : null

    return location
        ? location.terminals.map(t => {
              const deviceId = getSettingByKeyAndTerminalId(state, "CC_BOLT_DEVICE_ID", location.id, t.id)
              const audioEnabled = getSettingByKeyAndTerminalId(
                  state,
                  "CC_BOLT_DEVICE_AUDIO_ENABLED",
                  location.id,
                  t.id
              )
              const audioEnabledValue =
                  audioEnabled != null && audioEnabled.value != null && audioEnabled.value !== "false"
              return {
                  terminalId: t.id,
                  locationId: location.id,
                  CC_BOLT_DEVICE_ID: deviceId ? deviceId.value : undefined,
                  CC_BOLT_DEVICE_AUDIO_ENABLED: audioEnabledValue,
              }
          })
        : []
}

export type DejavooTerminalSettings = {
    terminalId: string
    locationId: string
    CC_DEJAVOO_AUTH_KEY: Maybe<string>
    CC_DEJAVOO_REGISTER_ID: Maybe<string>
    CC_TSYS_DEVICE_ID: Maybe<string>
}

export type TsysMibTerminalSettings = {
    terminalId: string
    locationId: string
    CC_TSYS_DEVICE_ID: Maybe<string>
}

export const getDejavooTerminalSettings = (state: StateObject): DejavooTerminalSettings[] => {
    const selectedLocationId = getSelectedLocationId(state)
    const location = selectedLocationId ? getLocationAuthDataById(state, selectedLocationId) : null

    return location
        ? location.terminals
              .filter(t => [TerminalType.Register, TerminalType.Mpos, TerminalType.Kiosk].includes(t.type))
              .map(t => {
                  const auth = getSettingByKeyAndTerminalId(state, "CC_DEJAVOO_AUTH_KEY", location.id, t.id)
                  const deviceId = getSettingByKeyAndTerminalId(state, "CC_TSYS_DEVICE_ID", location.id, t.id)
                  const registerId = getSettingByKeyAndTerminalId(state, "CC_DEJAVOO_REGISTER_ID", location.id, t.id)
                  return {
                      terminalId: t.id,
                      locationId: location.id,
                      CC_DEJAVOO_AUTH_KEY: auth ? auth.value : undefined,
                      CC_DEJAVOO_REGISTER_ID: registerId ? registerId.value : undefined,
                      CC_TSYS_DEVICE_ID: deviceId ? deviceId.value : undefined,
                  }
              })
        : []
}

export const getTsysMibTerminalSettings = (state: StateObject): TsysMibTerminalSettings[] => {
    const selectedLocationId = getSelectedLocationId(state)
    const location = selectedLocationId ? getLocationAuthDataById(state, selectedLocationId) : null
    return location
        ? location.terminals
              .filter(t => [TerminalType.Mib].includes(t.type))
              .map(t => {
                  const deviceId = getSettingByKeyAndTerminalId(state, "CC_TSYS_DEVICE_ID", location.id, t.id)
                  return {
                      terminalId: t.id,
                      locationId: location.id,
                      CC_TSYS_DEVICE_ID: deviceId?.value ?? undefined,
                  }
              })
        : []
}

export const getCashPaymentSettings = (state: StateObject): SettingFormItem => ({
    ...handleTerminalSettingFormItem(state, "PAYMENT_TYPES_CASH_ENABLED", "other", true),
    ...handleTerminalSettingFormItem(state, "CASH_TRANSACTIONS_ORDER_LIST_OPTIONS_ENABLED", "other", false),
    ...handleTerminalSettingFormItem(state, "ACCOUNT_BANK_AMOUNT", "other", 20000),
    ...handleTerminalSettingFormItem(state, "APP_TIP_ADJUST_OPENS_CASH_REGISTER", "other", false),
})

export const getServiceFeeSettings = (state: StateObject): SettingFormItem => ({
    ...handleSingleLocationSettingFormItem(state, "SERVICE_FEE_ENABLED", "other", false),
    ...handleSingleLocationSettingFormItem(state, "SERVICE_FEE_CUSTOM_TEXT", "string", null),
    ...handleSingleLocationSettingFormItem(state, "SERVICE_FEE_VALUE_TYPE", "string", "percentage"),
    ...getPercentageBasedSetting(
        handleSingleLocationSettingFormItem(state, "SERVICE_FEE_PERCENTAGE_VALUE", "string", null),
        "SERVICE_FEE_PERCENTAGE_VALUE"
    ),
    ...getCurrencyBasedSettingInDollars(
        handleSingleLocationSettingFormItem(state, "SERVICE_FEE_ABSOLUTE_VALUE", "other", null),
        "SERVICE_FEE_ABSOLUTE_VALUE"
    ),
    ...handleSingleLocationSettingFormItem(state, "CARD_SURCHARGE_ENABLED", "other", false),
    ...handleSingleLocationSettingFormItem(state, "CARD_SURCHARGE_CUSTOM_TEXT", "string", null),
    ...handleSingleLocationSettingFormItem(state, "RECEIPT_CARD_SURCHARGE_TOTAL_CUSTOM_TEXT", "string", null),
    ...handleSingleLocationSettingFormItem(state, "RECEIPT_DISPLAY_CARD_SURCHARGE_ON_CHECK_SALES", "other", true),
    ...handleSingleLocationSettingFormItem(state, "CARD_SURCHARGE_AUTO_APPLY", "other", false),
    ...handleSingleLocationSettingFormItem(state, "CARD_SURCHARGE_ENABLED_MIB", "other", false),
    ...handleSingleLocationSettingFormItem(state, "CARD_SURCHARGE_ENABLED_KIOSK", "other", false),
    ...getPercentageBasedSetting(
        handleSingleLocationSettingFormItem(state, "CARD_SURCHARGE_PERCENTAGE_VALUE", "string", 0),
        "CARD_SURCHARGE_PERCENTAGE_VALUE"
    ),
    ...getCurrencyBasedSettingInDollars(
        handleSingleLocationSettingFormItem(state, "CARD_SURCHARGE_AUTO_APPLY_LIMIT", "string", 0),
        "CARD_SURCHARGE_AUTO_APPLY_LIMIT"
    ),
    ...handleSingleLocationSettingFormItem(state, "CASH_DISCOUNT_ENABLED", "other", false),
    ...handleSingleLocationSettingFormItem(state, "CASH_DISCOUNT_CUSTOM_TEXT", "string", null),
    ...handleSingleLocationSettingFormItem(state, "RECEIPT_CASH_DISCOUNT_TOTAL_CUSTOM_TEXT", "string", null),
    ...handleSingleLocationSettingFormItem(state, "RECEIPT_DISPLAY_CASH_DISCOUNT_ON_CHECK_SALES", "other", true),
    ...handleSingleLocationSettingFormItem(state, "CASH_DISCOUNT_AUTO_APPLY", "other", false),
    ...handleSingleLocationSettingFormItem(state, "CASH_DISCOUNT_ENABLED_MIB", "other", false),
    ...handleSingleLocationSettingFormItem(state, "CASH_DISCOUNT_ENABLED_KIOSK", "other", false),
    ...handleSingleLocationSettingFormItem(state, "CASH_DISCOUNT_VALUE_TYPE", "string", null),
    ...getPercentageBasedSetting(
        handleSingleLocationSettingFormItem(state, "CASH_DISCOUNT_PERCENTAGE_VALUE", "string", 0),
        "CASH_DISCOUNT_PERCENTAGE_VALUE"
    ),
    ...getCurrencyBasedSettingInDollars(
        handleSingleLocationSettingFormItem(state, "CASH_DISCOUNT_ABSOLUTE_VALUE", "other", 0),
        "CASH_DISCOUNT_ABSOLUTE_VALUE"
    ),

    ...handleSingleLocationSettingFormItem(state, "PRICE_INCREASE_ENABLED", "other", false),
    ...handleSingleLocationSettingFormItem(state, "PRICE_INCREASE_ENABLED_POS", "other", false),
    ...handleSingleLocationSettingFormItem(state, "PRICE_INCREASE_ENABLED_POS_CUSTOM_ITEMS", "other", false),
    ...handleSingleLocationSettingFormItem(state, "PRICE_INCREASE_ENABLED_MIB", "other", false),
    ...handleSingleLocationSettingFormItem(state, "PRICE_INCREASE_ENABLED_KIOSK", "other", false),
    ...getPercentageBasedSetting(
        handleSingleLocationSettingFormItem(state, "PRICE_INCREASE_PERCENTAGE_VALUE", "string", 0),
        "PRICE_INCREASE_PERCENTAGE_VALUE"
    ),
})

export const isServiceFeeEnabledCardPaymentAgenda = (state: StateObject): boolean => {
    const selectedLocationsIds = getSelectedLocationIdsFromGlobalFilter(state)
    const serviceFeeResults = selectedLocationsIds?.map(id => isServiceFeeEnabledForLocation(state, id))
    return serviceFeeResults.some(fee => fee === true)
}

export const isServiceFeeEnabledForLocation = (state: StateObject, locationId: string) => {
    const setting = getSettingByKeyAndLocationId(state, "SERVICE_FEE_ENABLED", locationId)
    return setting ? JSON.parse(setting.value) : false
}

const getEnabledPercentageBasedSettingByKey = (
    state: StateObject,
    locationId: string,
    enablingKey: string,
    typeKey: string | null,
    valueKey: string
): number => {
    const isSettingEnabled = getSettingValueForLocationId(state, enablingKey, locationId, "other", false)[enablingKey]
        .value
    const isPercentageType = typeKey
        ? getSettingValueForLocationId(state, typeKey, locationId, "string", null)[typeKey].value === "percentage"
        : true
    const settingValue = getPercentageBasedSetting(
        getSettingValueForLocationId(state, valueKey, locationId, "string", null),
        valueKey
    )[valueKey].value

    if (isSettingEnabled && isPercentageType) {
        return isNaN(settingValue) ? 0 : Math.abs(settingValue)
    }
    return 0
}

export const getShiftReviewSettings = (state: StateObject): SettingFormItem => ({
    ...handleLocationSettingFormItem(state, "SHIFT_REVIEW_CARD_FEE_FOR_TIPS", "other", false),
    ...handleLocationSettingFormItem(state, "SHIFT_REVIEW_CARD_FEE_FOR_TIPS_PERCENTAGE_VALUE", "string", 0),
})

// This is the closest validation we can get now since webapp is not using settings sdk properly yet
export const GOOGLE_REVIEWS_AI_SETTING_KEY = "GOOGLE_REVIEWS_AI_REPLIES_ENABLED" satisfies TSetting

// Settings only superusers can see. Kept generic - the panel is a home for future ones.
export const getSuperuserSettings = (state: StateObject): SettingFormItem => ({
    ...handleLocationSettingFormItem(state, GOOGLE_REVIEWS_AI_SETTING_KEY, "other", false),
})

export const getMaximumServiceCardCashPercentageFee = (state: StateObject): number => {
    const locationIds = getSelectedLocationIdsFromGlobalFilter(state)

    const serviceFee = Math.max(
        ...locationIds.map(locationId =>
            getEnabledPercentageBasedSettingByKey(
                state,
                locationId,
                "SERVICE_FEE_ENABLED",
                "SERVICE_FEE_VALUE_TYPE",
                "SERVICE_FEE_PERCENTAGE_VALUE"
            )
        )
    )
    const cardSurcharge = Math.max(
        ...locationIds.map(locationId =>
            getEnabledPercentageBasedSettingByKey(
                state,
                locationId,
                "CARD_SURCHARGE_ENABLED",
                null,
                "CARD_SURCHARGE_PERCENTAGE_VALUE"
            )
        )
    )
    const cashDiscount = Math.max(
        ...locationIds.map(locationId =>
            getEnabledPercentageBasedSettingByKey(
                state,
                locationId,
                "CASH_DISCOUNT_ENABLED",
                "CASH_DISCOUNT_VALUE_TYPE",
                "CASH_DISCOUNT_PERCENTAGE_VALUE"
            )
        )
    )
    const itemPricesIncrease = Math.max(
        ...locationIds.map(locationId =>
            getEnabledPercentageBasedSettingByKey(
                state,
                locationId,
                "PRICE_INCREASE_ENABLED",
                null,
                "PRICE_INCREASE_PERCENTAGE_VALUE"
            )
        )
    )
    return Math.max(serviceFee, cardSurcharge, cashDiscount, itemPricesIncrease)
}

export const getExternalPaymentSettings = (state: StateObject): SettingFormItem => ({
    ...handleTerminalSettingFormItem(state, "PAYMENT_TYPES_EXTERNAL_ENABLED", "other", false),
    ...handleTerminalSettingFormItem(state, "EXTERNAL_TRANSACTIONS_ORDER_LIST_OPTIONS_ENABLED", "other", false),
    ...handleTerminalSettingFormItem(state, "PAYMENT_TYPES_EXTERNAL_TYPES", "array", ""),
})

// Make It Butter
export const handleMibSettingFormItem = (
    state: StateObject,
    key: string,
    type: Maybe<SettingType> = "other",
    defaultValue: Maybe<any> = null
): SettingFormItem => {
    const terminalId = getMibServicePickerSelectedId(state)
    const ass = terminalId ? getSettingsByKey(state, key).filter(s => s.terminal_id === terminalId) : []
    return getSettingFormItem(key, type, defaultValue, terminalId ? [terminalId] : [], ass)
}

export const getMakeItButterSettings = (state: StateObject): SettingFormItem => ({
    ...handleMibSettingFormItem(state, "MIB_DO_NOT_MARK_AS_CLOSED_IF_CLOSED", "other", false),
    ...handleMibSettingFormItem(state, "MIB_HIDE_SERVICE_FROM_LOCATION_PICKER_PAGE", "other", false),
    ...handleMibSettingFormItem(state, "MIB_EMAIL_WITH_EVERY_ORDER", "other", true),
    ...handleMibSettingFormItem(state, "MIB_DESKTOP_DISPLAY_BUSINESS_INFO_BOX", "other", true),
    ...handleMibSettingFormItem(state, "MIB_AUTO_CLOSE", "other", false),
    ...handleMibSettingFormItem(state, "MIB_ABILITY_TO_DELAY_ORDERS_ENABLED", "other", false),
    ...handleMibSettingFormItem(state, "MIB_AUTO_ACCEPT_ALL", "other", false),
    ...handleMibSettingFormItem(state, "MIB_AUTO_ACCEPT_ONLY_WHEN_SIGNED_IN", "other", false),
    ...getMibColorSetting(state),
    ...handleMibSettingFormItem(state, "MIB_MAIN_MENU_ICON", "string", MainMenuIconValue.Menu),
    ...handleMibSettingFormItem(state, "MIB_ACCEPT_CARD_FOR_DELIVERY", "other", true),
    ...handleMibSettingFormItem(state, "MIB_DELIVERY_ACCEPT_CARD_PAYMENTS", "other", true),
    ...handleMibSettingFormItem(state, "MIB_ACCEPT_CARD_FOR_PICKUP", "other", true),
    ...handleMibSettingFormItem(state, "MIB_ACCEPT_CASH_FOR_DELIVERY", "other", true),
    ...handleMibSettingFormItem(state, "MIB_ACCEPT_CASH_FOR_PICKUP", "other", true),
    ...handleMibSettingFormItem(state, "MIB_ACCEPT_TIPS_FOR_DELIVERY", "other", true),
    ...handleMibSettingFormItem(state, "MIB_ACCEPT_TIPS_FOR_PICKUP", "other", true),
    ...handleMibSettingFormItem(state, "MIB_ESTIMATED_DELIVERY_TIME", "other", 0),
    ...handleMibSettingFormItem(state, "MIB_ESTIMATED_PICKUP_TIME", "other", 0),
    ...handleMibSettingFormItem(state, "MIB_LAST_DELIVERY_TIME", "other", null),
    ...handleMibSettingFormItem(state, "MIB_LAST_PICKUP_TIME", "other", null),
    ...handleMibSettingFormItem(state, "MIB_DELIVERY_ENABLED", "other", true),
    ...handleMibSettingFormItem(state, "MIB_PICKUP_ENABLED", "other", true),
    ...handleMibSettingFormItem(state, "FUTURE_ORDERING_DATE_ONLY", "other", false),
    ...handleMibSettingFormItem(state, "MIB_IS_FUTURE_ORDER_PICKUP_VALUE", "other", false),
    ...handleMibSettingFormItem(state, "MIB_ACCEPT_ONLY_FUTURE_ORDER_FOR_DELIVERY", "other", false),
    ...handleMibSettingFormItem(state, "MIB_ACCEPT_ONLY_FUTURE_ORDER_FOR_PICKUP", "other", false),
    ...handleMibSettingFormItem(state, "MIB_FUTURE_ORDER_LEAD_TIME_MINUTES_DELIVERY", "other", 0),
    ...handleMibSettingFormItem(state, "MIB_FUTURE_ORDER_LEAD_TIME_ACTIVE_DELIVERY", "other", false),
    ...handleMibSettingFormItem(state, "MIB_FUTURE_ORDER_LEAD_TIME_MINUTES_PICKUP", "other", 0),
    ...handleMibSettingFormItem(state, "MIB_FUTURE_ORDER_LEAD_TIME_ACTIVE_PICKUP", "other", false),
    ...handleMibSettingFormItem(state, "MIB_IS_FUTURE_ORDER_DELIVERY_VALUE", "other", false),
    ...handleMibSettingFormItem(state, "MIB_PROMO_CODE_SP_ID", "string", null),
    ...handleMibSettingFormItem(state, "MIB_FIRST_TIME_PROMO_DISCOUNT_SP_ID", "string", null),
    ...handleMibSettingFormItem(state, "MO_FIRST_ORDER_PROMO_DISCOUNT_SP_ID", "string", null),
    ...handleMibSettingFormItem(state, "MIB_FUTURE_ORDER_DAYS_DELIVERY_VALUE", "other", FUTURE_ORDERING_DAYS_DEFAULT),
    ...handleMibSettingFormItem(state, "MIB_FUTURE_ORDER_DAYS_PICKUP_VALUE", "other", FUTURE_ORDERING_DAYS_DEFAULT),
    ...handleMibSettingFormItem(state, "MIB_PROMO_CODE_WORD", "string", "BUTTER"),
    ...handleMibSettingFormItem(state, "MIB_DOORDASH_PICKUP_DELAY_MINUTES", "other", 0),
    ...handleMibSettingFormItem(state, "MIB_DELIVERY_CONTACT_FREE_OPTION_ENABLED", "other", false),
    ...handleMibSettingFormItem(state, "MIB_PICKUP_CURBSIDE_OPTION_ENABLED", "other", false),
    ...handleMibSettingFormItem(state, "MIB_PICKUP_CURBSIDE_OPTION_GUIDANCE", "other", false),
    ...getMibDollarBasedSetting(state, "MIB_MIN_DELIVERY_VALUE"),
    ...getMibDollarBasedSetting(state, "MIB_MIN_PICKUP_VALUE"),
    ...getMibOrderingMode(state),
    ...handleMibSettingFormItem(state, "MIB_TIPPING_METHOD", "string", "smart"),
    ...handleMibSettingFormItem(state, MIB_TIPPING_METHOD_PERCENTAGES, "array", "15000,18000,20000,25000"),
    ...handleMibSettingFormItem(state, TIPPING_PRESELECTED_OPTION_INDEX, "other", null),
    ...handleMibSettingFormItem(state, "MIB_BANNER_MESSAGE", "string", null),
    ...handleMibSettingFormItem(state, "MIB_LAYOUT_TEMPLATE", "string", "standard"),
    ...handleMibSettingFormItem(state, "MIB_ABILITY_TO_PAY_WITH_EGC", "other", false),
    ...handleMibSettingFormItem(state, "MIB_ECO_FRIENDLY_OPTION_ENABLED", "other", false),
    ...handleMibSettingFormItem(state, "MIB_ECO_FRIENDLY_OPTION_DEFAULT_CHECKED", "other", false),
    ...handleMibSettingFormItem(state, "MIB_FORBID_COLLECT_EMAILS", "other", false),
    ...handleMibSettingFormItem(state, "TIP_CALCULATION_METHOD", "string", TipCalculationMethod.Subtotal),
})

export const DisplayCountSalesReceiptTypeEnum = {
    Off: "off",
    Count: "count",
    Pieces: "pieces",
}

export type DisplayCountSalesReceiptType =
    (typeof DisplayCountSalesReceiptTypeEnum)[keyof typeof DisplayCountSalesReceiptTypeEnum]

export const getDisplayCountOnSalesReceipt = (state: StateObject): SettingFormItem => {
    const res = handleTerminalSettingFormItem(state, "RECEIPT_DISPLAY_COUNT_ON_SALES", "string", false)

    if (!res || res.RECEIPT_DISPLAY_COUNT_ON_SALES.value == null) {
        return res
    }

    if (res && res.indeterminate) {
        return {
            ...res,
            RECEIPT_DISPLAY_COUNT_ON_SALES: {
                // @ts-expect-error
                indeterminate: res.indeterminate,
                value: undefined,
            },
        }
    }

    const settingVal = res && res.RECEIPT_DISPLAY_COUNT_ON_SALES.value
    let finalValue = false

    if (settingVal === DisplayCountSalesReceiptTypeEnum.Off || settingVal === "false") {
        finalValue = false
    } else if (
        settingVal === DisplayCountSalesReceiptTypeEnum.Count ||
        settingVal === DisplayCountSalesReceiptTypeEnum.Pieces ||
        settingVal === "true"
    ) {
        finalValue = true
    } else {
        finalValue = settingVal
    }

    return {
        ...res,
        RECEIPT_DISPLAY_COUNT_ON_SALES: {
            indeterminate: false,
            value: finalValue,
        },
    }
}

export const getMibOrderingMode = (state: StateObject): SettingFormItem => {
    const res = handleMibSettingFormItem(state, "MIB_ORDERING_MODE", "string", "delivery")

    if (!res || res.MIB_ORDERING_MODE.value == null) {
        return res
    }

    return {
        ...res,
        MIB_ORDERING_MODE: {
            indeterminate: false,
            value: res.MIB_ORDERING_MODE.value,
        },
    }
}

export const getMibUrlImageTypeValue = (state: StateObject, key: string): SettingFormItem => {
    const returnObject = {
        [key]: {
            value: null,
            indeterminate: false,
        },
    }
    const mibIconsSetting = handleMibSettingFormItem(state, key, "arrayOfObjects", null)

    if (mibIconsSetting[key].value && mibIconsSetting[key].value[0]) {
        const val = mibIconsSetting[key].value[0]
        return { ...returnObject, [key]: { ...returnObject[key], value: val } }
    }

    return {
        ...returnObject,
        [key]: {
            ...returnObject[key],
            value: mibIconsSetting[key].value,
            indeterminate: mibIconsSetting[key].indeterminate,
        },
    }
}

export const getMibDollarBasedSetting = (state: StateObject, key: string) => {
    const res = handleMibSettingFormItem(state, key, "string", null)

    if (!res || res[key].value == null) {
        return res
    }

    return {
        ...res,
        [key]: {
            indeterminate: false,
            value: res[key].value / 100,
        },
    }
}

export const getMibColorSetting = (state: StateObject) => {
    const res = handleMibSettingFormItem(state, "MIB_PRIMARY_COLOR", "string", null)

    if (!res || res.MIB_PRIMARY_COLOR.value == null) {
        return res
    }

    const val = res.MIB_PRIMARY_COLOR.value.substring(1)
    return {
        ...res,
        MIB_PRIMARY_COLOR: {
            indeterminate: false,
            value: val,
        },
    }
}

export const getDiscountSpecialPricingValue = (state: StateObject): Maybe<number> => {
    const setting = handleMibSettingFormItem(state, "MIB_PROMO_CODE_SP_ID", "string", null)
    const specialPricings = getPricingsData(state)

    if (!specialPricings) {
        return null
    }

    if (setting && setting.MIB_PROMO_CODE_SP_ID.value && !setting.MIB_PROMO_CODE_SP_ID.indeterminate) {
        const result = specialPricings.data.find(sp => sp.id === setting.MIB_PROMO_CODE_SP_ID.value)
        return result && result.percentage ? result.percentage / 1000 : null
    }

    return null
}

export const getMoFirstOrderDiscountPricing = (state: StateObject) => {
    const terminalId = getMibServicePickerSelectedId(state)
    const setting = terminalId
        ? getSettingByKeyAndTerminalId(state, "MO_FIRST_ORDER_PROMO_DISCOUNT_SP_ID", null, terminalId)
        : null
    return getPricingById(state, setting?.value)
}

export const getFirstTimeDiscountSpecialPricingValue = (state: StateObject): Maybe<number> => {
    const setting = handleMibSettingFormItem(state, "MIB_FIRST_TIME_PROMO_DISCOUNT_SP_ID", "string", null)
    const specialPricings = getPricingsData(state)

    if (!specialPricings) {
        return null
    }

    if (
        setting &&
        setting.MIB_FIRST_TIME_PROMO_DISCOUNT_SP_ID.value &&
        !setting.MIB_FIRST_TIME_PROMO_DISCOUNT_SP_ID.indeterminate
    ) {
        const result = specialPricings.data.find(sp => sp.id === setting.MIB_FIRST_TIME_PROMO_DISCOUNT_SP_ID.value)
        return result && result.percentage ? result.percentage / 1000 : null
    }

    return null
}

export const getMakeItButterEnabledSetting = (state: StateObject) => {
    return getSettingsByKey(state, "MIB_ENABLED")
}

export const makeItButterActiveLocationsSettings = (state: StateObject) => {
    const settings = getMakeItButterEnabledSetting(state)
    return settings.filter(s => JSON.parse(s.value) === true)
}

export const getSettingIsLoyaltyActiveFigure = (state: StateObject): boolean => {
    const setting = getSettingByKey(state, "LOYALTY_IS_ACTIVE_FIGURE")

    if (setting && setting.value) {
        return JSON.parse(setting.value)
    }

    return false
}

export const getSettingIsLoyaltyActiveMib = (state: StateObject): boolean => {
    const setting = getSettingByKey(state, "LOYALTY_IS_ACTIVE_MIB")

    if (setting && setting.value) {
        return JSON.parse(setting.value)
    }

    return false
}
// Home Page Appearance

export const isBusinessHomePageFormOpened = (state: StateObject) => {
    const pathInfo = getCurrentPath(state)
    const urlQuery = pathInfo.query
    return !!(urlQuery && urlQuery.modal && urlQuery.modal === "homepageAppearance")
}

export const getHomePageBusinnesLogo = (state: StateObject) => {
    const accountImages = getAccountImages(state)
    const image = accountImages.find(i => i.type === "mib-business-homepage-logo")
    return image || null
}

export const getHomePageBusinnesBackgroundImage = (state: StateObject) => {
    const accountImages = getAccountImages(state)
    const image = accountImages.find(i => i.type === "mib-business-homepage-background-desktop")
    return image || null
}

export const getTerminalIdsForTerminalPickerInit = (state: StateObject, terminalIds: string[]): string[] =>
    pipeline(
        terminalIds,
        // @ts-expect-error
        filter((id: string) => {
            const terminal = getTerminalAuthDataById(state, id)

            if (!terminal) {
                return false
            }

            if (shouldFilterMibTerminals(state) && terminal.type === TerminalType.Mib) {
                return false
            }

            if (
                terminal.type === TerminalType.Kds ||
                terminal.type === TerminalType.Aggregator ||
                terminal.type === TerminalType.Osb
            ) {
                return false
            }

            return id
        }),
        reject(isNil)
    ) as string[]
